/**
 * Botones para llevarse cada informe a un archivo.
 *
 * ---------------------------------------------------------------------------
 *  Por qué es un botón y no un enlace
 * ---------------------------------------------------------------------------
 *
 * Antes eran enlaces (`<a href>`), y **no funcionaban**: un enlace no puede llevar la
 * cabecera de autorización —el token vive en memoria— y la cookie solo se envía a
 * `/api/auth`. El servidor contestaba «Debes iniciar sesión» y el navegador enseñaba ese
 * texto en vez de descargar nada.
 *
 * Ahora se pide el archivo como cualquier otra cosa y se entrega al navegador. Ver
 * `downloadFile` en `lib/api.ts`.
 */

import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { downloadFile } from '../lib/api';

/** Un informe que se puede descargar. */
export interface ReportExport {
  /** El nombre que entiende el servidor. */
  entity: string;
  /** Cómo se llama en pantalla. */
  label: string;
  /** Qué lleva dentro, en una línea. */
  detail: string;
}

/**
 * Los informes que se pueden sacar.
 *
 * El orden va de lo que más se usa a lo que menos. «Movimientos» primero porque es lo
 * que se le pasa a un contador.
 */
export const REPORT_EXPORTS: readonly ReportExport[] = [
  { entity: 'movimientos', label: 'Movimientos', detail: 'Todos tus gastos e ingresos, con el desglose' },
  { entity: 'gastos-fijos', label: 'Gastos fijos', detail: 'Cobros recurrentes y cuándo toca el próximo' },
  { entity: 'deudas', label: 'Deudas', detail: 'Saldo, cuota, interés y meses en mora' },
  { entity: 'fiado', label: 'Fiado', detail: 'Lo que debes en cada tienda' },
  { entity: 'cuentas', label: 'Cuentas', detail: 'Saldo inicial y saldo actual de cada una' },
  { entity: 'presupuestos', label: 'Presupuestos', detail: 'Lo que asignaste a cada categoría por mes' },
  { entity: 'metas', label: 'Metas de ahorro', detail: 'Objetivo, ahorrado y cuánto falta' },
  { entity: 'categorias', label: 'Categorías', detail: 'El catálogo completo, con las tuyas' },
];

/** Un botón que descarga un informe. */
export function ExportButton({
  report,
  className = '',
}: {
  report: ReportExport;
  className?: string;
}): React.JSX.Element {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');

  const run = async (): Promise<void> => {
    setState('busy');
    try {
      await downloadFile(`/api/reports/export/${report.entity}`, `eldescuadre-${report.entity}.csv`);
      setState('idle');
    } catch {
      // Se dice en el propio botón: si no, el usuario pulsa y no pasa nada visible.
      setState('error');
    }
  };

  return (
    <button
      type="button"
      className={`ed-btn-secondary justify-start !min-h-[44px] text-left ${className}`}
      disabled={state === 'busy'}
      onClick={() => void run()}
      title={report.detail}
    >
      {state === 'busy' ? (
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
      ) : (
        <Download size={16} aria-hidden="true" />
      )}
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">
          {state === 'error' ? 'No se pudo. Reintentar' : report.label}
        </span>
        <span className="block truncate text-[11px] font-normal text-ed-text-muted">
          {state === 'error' ? 'Comprueba tu conexión' : report.detail}
        </span>
      </span>
    </button>
  );
}

/** La lista completa, para los ajustes: así no hay informe que no se pueda sacar. */
export function ExportReportList(): React.JSX.Element {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {REPORT_EXPORTS.map((report) => (
        <ExportButton key={report.entity} report={report} />
      ))}
    </div>
  );
}

/**
 * El respaldo completo, en JSON.
 *
 * Va aparte de los CSV porque no es un informe: es **todo**, para poder restaurarlo
 * después. El nombre lo pone el servidor con la fecha del día.
 */
export function BackupButton(): React.JSX.Element {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');

  const run = async (): Promise<void> => {
    setState('busy');
    try {
      await downloadFile('/api/reports/export.json', 'eldescuadre-respaldo.json');
      setState('idle');
    } catch {
      setState('error');
    }
  };

  return (
    <button
      type="button"
      className="ed-btn-secondary justify-start !min-h-[44px] text-left"
      disabled={state === 'busy'}
      onClick={() => void run()}
      title="Todo tu historial, para poder restaurarlo después"
    >
      {state === 'busy' ? (
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
      ) : (
        <Download size={16} aria-hidden="true" />
      )}
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">
          {state === 'error' ? 'No se pudo. Reintentar' : 'Respaldo completo'}
        </span>
        <span className="block truncate text-[11px] font-normal text-ed-text-muted">
          {state === 'error' ? 'Comprueba tu conexión' : 'Todo tu historial, en JSON'}
        </span>
      </span>
    </button>
  );
}
